import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Plus, PencilSimple, Trash, Plug, ArrowsClockwise, CheckCircle, XCircle } from "@phosphor-icons/react";

const KINDS = [
  { v: "veeam_vbr", l: "Veeam VBR",        desc: "Veeam Backup & Replication REST API (v11+)." },
  { v: "acronis",   l: "Acronis Cyber Cloud", desc: "OAuth2 client_credentials against your datacenter URL." },
  { v: "datto_rmm", l: "Datto RMM",        desc: "Datto RMM REST API — uses your API key + secret." },
  { v: "generic",   l: "Generic JSON",     desc: "Any vendor — fetch a JSON URL, point to job array, map fields." },
];

const EMPTY = {
  name: "", kind: "veeam_vbr", enabled: true, client_id: "", sync_interval_minutes: 30,
  alert_channel_ids: [],
  veeam_base_url: "", veeam_username: "", veeam_password: "",
  acronis_datacenter_url: "", acronis_client_id: "", acronis_client_secret: "",
  datto_api_url: "", datto_api_key: "", datto_api_secret: "",
  generic_url: "", generic_auth_header: "", generic_jobs_jsonpath: "$",
  generic_name_field: "name", generic_status_field: "status",
};

export default function VendorIntegrations() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [v, c, ch] = await Promise.all([
        api.get("/vendor-integrations"),
        api.get("/clients"),
        api.get("/alert-channels"),
      ]);
      setItems(v.data); setClients(c.data); setChannels(ch.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); setError(""); };
  const openEdit = (v) => {
    setForm({
      ...EMPTY, ...v,
      client_id: v.client_id || "",
      alert_channel_ids: v.alert_channel_ids || [],
    });
    setEditing(v); setError("");
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form, client_id: form.client_id || null,
        sync_interval_minutes: Number(form.sync_interval_minutes) || 30 };
      if (editing === "new") await api.post("/vendor-integrations", payload);
      else await api.put(`/vendor-integrations/${editing.id}`, payload);
      setEditing(null); await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete vendor integration?")) return;
    await api.delete(`/vendor-integrations/${id}`); await load();
  };

  const sync = async (id) => {
    setSyncing(s => ({ ...s, [id]: true }));
    try { await api.post(`/vendor-integrations/${id}/sync`); await load(); }
    finally { setSyncing(s => ({ ...s, [id]: false })); }
  };

  const kindMeta = (v) => KINDS.find(k => k.v === v) || KINDS[0];
  const clientName = (id) => clients.find(c => c.id === id)?.name || "—";

  return (
    <Layout>
      <PageHeader
        title="Vendor integrations"
        subtitle="Pull backup status directly from vendor APIs — bypass email entirely"
        actions={
          <button onClick={openNew} data-testid="vendors-new-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold transition-colors">
            <Plus size={14} weight="bold" /> New integration
          </button>
        }
      />
      <div className="p-6">
        {loading ? <div className="text-sm text-slate-500">loading…</div>
         : items.length === 0 ? (
          <div className="sw-card border-dashed p-16 text-center">
            <Plug size={36} weight="duotone" className="text-brand-cyan mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold tracking-tight mb-1">No vendor integrations yet</h3>
            <p className="text-sm text-slate-400 mb-4">Connect Veeam, Acronis, Datto RMM, or any JSON endpoint for direct API status.</p>
            <button onClick={openNew} data-testid="vendors-empty-cta"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">
              <Plus size={14} weight="bold" /> Add integration
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vendors-grid">
            {items.map((v) => {
              const meta = kindMeta(v.kind);
              const lastOk = v.last_sync_status?.startsWith("ok");
              return (
                <div key={v.id} className="sw-card p-5 sw-card-hover">
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <div className="font-display font-bold text-base truncate">{v.name}</div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{meta.l}</div>
                    </div>
                    <span className={`text-[10px] font-semibold uppercase tracking-[0.15em] ${v.enabled ? "text-emerald-400" : "text-slate-400"}`}>
                      {v.enabled ? "ON" : "OFF"}
                    </span>
                  </div>
                  <div className="text-xs text-slate-400 space-y-1 mb-3 min-h-[42px]">
                    <div>Client: <span className="text-slate-200">{clientName(v.client_id)}</span></div>
                    <div>Every {v.sync_interval_minutes}m · {(v.alert_channel_ids || []).length} channel(s)</div>
                  </div>
                  {v.last_sync_status && (
                    <div className={`text-[11px] mb-3 px-2 py-1 rounded inline-flex items-center gap-1.5 ${
 lastOk ?"text-emerald-400 bg-emerald-500/10 border border-emerald-500/30"
 :"text-red-400 bg-red-500/10 border border-red-500/30"}`}>
                      {lastOk ? <CheckCircle size={11} weight="fill" /> : <XCircle size={11} weight="fill" />}
                      {v.last_sync_status} · {v.last_sync_at ? new Date(v.last_sync_at).toLocaleTimeString() : "—"}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <button onClick={() => sync(v.id)} disabled={syncing[v.id]} data-testid={`vendor-sync-${v.id}`}
                      className="text-xs inline-flex items-center gap-1.5 text-brand-cyan hover:text-white">
                      <ArrowsClockwise size={12} weight="bold" />
                      {syncing[v.id] ? "Syncing…" : "Sync now"}
                    </button>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(v)} data-testid={`vendor-edit-${v.id}`}
                        className="p-1.5 text-slate-300 hover:text-white"><PencilSimple size={14} /></button>
                      <button onClick={() => remove(v.id)} data-testid={`vendor-delete-${v.id}`}
                        className="p-1.5 text-slate-300 hover:text-red-400"><Trash size={14} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-30 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="sw-card w-full max-w-xl p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="vendor-form">
            <h3 className="font-display text-lg font-bold tracking-tight">{editing === "new" ? "New vendor integration" : "Edit integration"}</h3>
            <FieldV label="Name" required>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name-input" />
            </FieldV>
            <FieldV label="Vendor type">
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="vendor-kind-select">
                {KINDS.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
              </select>
            </FieldV>
            <p className="text-xs text-slate-400 -mt-1">{kindMeta(form.kind).desc}</p>

            {form.kind === "veeam_vbr" && (
              <>
                <FieldV label="Base URL" required><input required value={form.veeam_base_url} placeholder="https://vbr.example.com:9419" onChange={(e) => setForm({ ...form, veeam_base_url: e.target.value })} /></FieldV>
                <div className="grid grid-cols-2 gap-3">
                  <FieldV label="Username" required><input required value={form.veeam_username} onChange={(e) => setForm({ ...form, veeam_username: e.target.value })} /></FieldV>
                  <FieldV label="Password"><input type="password" value={form.veeam_password} placeholder={editing !== "new" ? "leave blank to keep" : ""} onChange={(e) => setForm({ ...form, veeam_password: e.target.value })} /></FieldV>
                </div>
              </>
            )}
            {form.kind === "acronis" && (
              <>
                <FieldV label="Datacenter URL" required><input required value={form.acronis_datacenter_url} placeholder="https://eu-cloud.acronis.com" onChange={(e) => setForm({ ...form, acronis_datacenter_url: e.target.value })} /></FieldV>
                <div className="grid grid-cols-2 gap-3">
                  <FieldV label="Client ID" required><input required value={form.acronis_client_id} onChange={(e) => setForm({ ...form, acronis_client_id: e.target.value })} /></FieldV>
                  <FieldV label="Client Secret"><input type="password" value={form.acronis_client_secret} placeholder={editing !== "new" ? "leave blank to keep" : ""} onChange={(e) => setForm({ ...form, acronis_client_secret: e.target.value })} /></FieldV>
                </div>
              </>
            )}
            {form.kind === "datto_rmm" && (
              <>
                <FieldV label="API URL" required><input required value={form.datto_api_url} placeholder="https://merlot-api.centrastage.net" onChange={(e) => setForm({ ...form, datto_api_url: e.target.value })} /></FieldV>
                <div className="grid grid-cols-2 gap-3">
                  <FieldV label="API Key" required><input required value={form.datto_api_key} onChange={(e) => setForm({ ...form, datto_api_key: e.target.value })} /></FieldV>
                  <FieldV label="API Secret"><input type="password" value={form.datto_api_secret} placeholder={editing !== "new" ? "leave blank to keep" : ""} onChange={(e) => setForm({ ...form, datto_api_secret: e.target.value })} /></FieldV>
                </div>
              </>
            )}
            {form.kind === "generic" && (
              <>
                <FieldV label="JSON URL" required><input required value={form.generic_url} placeholder="https://api.example.com/v1/backups" onChange={(e) => setForm({ ...form, generic_url: e.target.value })} /></FieldV>
                <FieldV label="Authorization header"><input value={form.generic_auth_header} placeholder='Bearer xyz... or Basic base64...' onChange={(e) => setForm({ ...form, generic_auth_header: e.target.value })} /></FieldV>
                <div className="grid grid-cols-3 gap-3">
                  <FieldV label="JSON path (array)"><input value={form.generic_jobs_jsonpath} placeholder="$.data.jobs" onChange={(e) => setForm({ ...form, generic_jobs_jsonpath: e.target.value })} /></FieldV>
                  <FieldV label="Name field"><input value={form.generic_name_field} onChange={(e) => setForm({ ...form, generic_name_field: e.target.value })} /></FieldV>
                  <FieldV label="Status field"><input value={form.generic_status_field} onChange={(e) => setForm({ ...form, generic_status_field: e.target.value })} /></FieldV>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
              <FieldV label="Client (tag)">
                <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                  <option value="">(none — global)</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </FieldV>
              <FieldV label="Sync every (minutes)">
                <input type="number" min="5" value={form.sync_interval_minutes} onChange={(e) => setForm({ ...form, sync_interval_minutes: e.target.value })} />
              </FieldV>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">Alert channels</label>
              {channels.length === 0 ? (
                <div className="text-xs text-slate-500">No channels yet. Create one in Alert channels.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {channels.map(ch => {
                    const on = form.alert_channel_ids.includes(ch.id);
                    return (
                      <button key={ch.id} type="button"
                        onClick={() => setForm(f => ({ ...f, alert_channel_ids: on
                          ? f.alert_channel_ids.filter(x => x !== ch.id)
                          : [...f.alert_channel_ids, ch.id] }))}
                        className={`text-xs px-2 py-1 rounded border ${on ? "border-brand-cyan text-brand-cyan bg-brand-cyan/10" : "border-border text-slate-300 hover:bg-white/5"}`}>
                        {ch.type} · {ch.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-200 pt-2">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="vendor-enabled-checkbox" />
              Enabled
            </label>
            {error && <div className="text-xs text-red-400">{error}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={() => setEditing(null)}
                className="px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md">Cancel</button>
              <button type="submit" data-testid="vendor-save-btn"
                className="px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">Save</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}

function FieldV({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {React.cloneElement(children, {
        className: "w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan transition-colors",
      })}
    </div>
  );
}
